import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * CORE-BJ — is the signed-in user a designated SUPPORT OPERATOR?
 *
 * Support access is its own explicit designation. It is deliberately NOT
 * derived from a shop role, and NOT from `internal_admins` — that registry is
 * the platform-wide staff/super-admin authority with many unrelated consumers,
 * and it keeps governing everything it governed before. It simply no longer
 * implies support access.
 *
 * This is UI eligibility only. The database is the security boundary: RLS on
 * `support_sessions` and the impersonation RPC both ask `is_support_operator`
 * independently, so a client that lies about this learns nothing.
 *
 * Calls the RPC with no argument so it defaults to `auth.uid()` — a caller can
 * determine THEIR OWN eligibility without the API becoming a directory of
 * privileged staff.
 */
export function useIsSupportOperatorForUserId(userId: string | null) {
  return useQuery({
    // Sentinel for the signed-out case so it cannot collide with a
    // signed-in user's cached entry (mirrors useIsInternalAdmin).
    queryKey: ['is-support-operator', userId ?? 'anon'],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await (supabase as any).rpc('is_support_operator');
      // Fail closed: an error must never read as "eligible".
      if (error) {
        console.warn('[useIsSupportOperator] eligibility check failed:', error);
        return false;
      }
      return data === true;
    },
  });
}

export function useIsSupportOperator() {
  const { user } = useAuth();
  return useIsSupportOperatorForUserId(user?.id ?? null);
}
